/**
 * User Preferences Utilities
 * 
 * Resolution order: App localStorage override → App DB override → Auth global default → System default
 */

export type Theme = 'light' | 'dark' | 'system'

export interface UserPreferences {
  theme: Theme
  language: string
  timezone: string
}

export const SYSTEM_DEFAULTS: UserPreferences = {
  theme: 'system',
  language: 'en',
  timezone: 'UTC',
}

/**
 * Resolve a single preference value using the priority chain:
 * appOverride → globalDefault → systemDefault
 */
export function resolvePreference<K extends keyof UserPreferences>(
  key: K,
  options: {
    appOverride?: UserPreferences[K] | null
    globalDefault?: UserPreferences[K] | null
    systemDefault?: UserPreferences[K]
  }
): UserPreferences[K] {
  const { appOverride, globalDefault, systemDefault } = options
  if (appOverride != null && appOverride !== '') return appOverride
  if (globalDefault != null && globalDefault !== '') return globalDefault
  return systemDefault ?? SYSTEM_DEFAULTS[key]
}

/**
 * Resolve all preferences at once.
 */
export function resolveAllPreferences(options: {
  appOverrides?: Partial<UserPreferences> | null
  globalDefaults?: Partial<UserPreferences> | null
}): UserPreferences {
  const { appOverrides, globalDefaults } = options
  return {
    theme: resolvePreference('theme', {
      appOverride: appOverrides?.theme,
      globalDefault: globalDefaults?.theme,
    }),
    language: resolvePreference('language', {
      appOverride: appOverrides?.language,
      globalDefault: globalDefaults?.language,
    }),
    timezone: resolvePreference('timezone', {
      appOverride: appOverrides?.timezone,
      globalDefault: globalDefaults?.timezone,
    }),
  }
}

/**
 * Get the user's timezone string (IANA format).
 * Falls back to browser timezone, then UTC.
 */
export function getUserTimezone(preferences?: Partial<UserPreferences> | null): string {
  if (preferences?.timezone) return preferences.timezone
  // Try browser timezone
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) return tz
  } catch {
    // Intl not available
  }
  return 'UTC'
}

/**
 * Format a date in the user's timezone using Intl.DateTimeFormat.
 */
export function formatDateInUserTimezone(
  date: Date | string,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
}

/**
 * Format a date (date only, no time) in the user's timezone.
 */
export function formatDateOnlyInUserTimezone(
  date: Date | string,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  }
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
}

/**
 * Build the localStorage key for an app-specific preference.
 * Convention: {appSlug}:{setting}  (e.g. playbook:theme)
 */
export function getPreferenceStorageKey(appSlug: string, setting: string): string {
  return `${appSlug}:${setting}`
}

/**
 * The languages the preference select offers, and the only values a stored
 * language preference may take.
 *
 * It lives here rather than beside the select because the Auth service
 * validates writes against the same list, and a backend cannot import a React
 * package. `packages/ui` re-exports it so existing importers keep working.
 */
export const PREFERENCE_LANGUAGES = ['en', 'zh'] as const
export type PreferenceLanguage = (typeof PREFERENCE_LANGUAGES)[number]

/** The theme values a stored preference may take. */
export const PREFERENCE_THEMES: readonly Theme[] = ['light', 'dark', 'system']

/**
 * Is this a timezone the runtime can actually format in?
 *
 * `Intl.DateTimeFormat` throws a RangeError on an unknown zone, which is the
 * only reliable check — the IANA list is the runtime's, not ours, and hardcoding
 * one would reject zones added after this file was written.
 */
export function isValidTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

export interface PreferencePatchResult {
  /** Values to store. A key mapped to `null` is a request to clear it. */
  patch: Record<string, string | null>
  /** Keys the caller sent with a value this system does not accept. */
  invalidKeys: string[]
}

/**
 * Validate an incoming preferences patch at the app boundary.
 *
 * Three cases per key, mirroring the profile-metadata helper next door:
 * omitted is left untouched, a valid value is stored, and `null` / `''` clears
 * the stored value so the next layer down (the account-wide default, then the
 * system default) answers again.
 *
 * Unknown keys are dropped rather than reported — a client sending a key this
 * version does not know is not an error the user can act on. A *known* key with
 * a bad value is reported, because silently dropping it would report success on
 * a write that did not happen.
 */
export function validatePreferencePatch(input: unknown): PreferencePatchResult {
  const patch: Record<string, string | null> = {}
  const invalidKeys: string[] = []

  if (!input || typeof input !== 'object') return { patch, invalidKeys }
  const bag = input as Record<string, unknown>

  const isClear = (value: unknown) =>
    value === null || (typeof value === 'string' && value.trim() === '')

  const accept = (key: keyof UserPreferences, valid: (value: string) => boolean) => {
    const value = bag[key]
    if (value === undefined) return
    if (isClear(value)) {
      patch[key] = null
      return
    }
    if (typeof value !== 'string' || !valid(value.trim())) {
      invalidKeys.push(key)
      return
    }
    patch[key] = value.trim()
  }

  accept('theme', (v) => (PREFERENCE_THEMES as readonly string[]).includes(v))
  accept('language', (v) => (PREFERENCE_LANGUAGES as readonly string[]).includes(v))
  accept('timezone', isValidTimezone)

  // `Intl` accepts a zone id in any case ('asia/tokyo', 'utc'), but every
  // picker matches its options by exact string, so a non-canonical spelling
  // stored here would show as "Select timezone" for a preference that is set.
  // Store the id the runtime itself reports.
  if (typeof patch.timezone === 'string') patch.timezone = canonicalTimezone(patch.timezone)

  return { patch, invalidKeys }
}

/** The runtime's own spelling of a zone id it has already accepted. */
export function canonicalTimezone(value: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value }).resolvedOptions().timeZone
  } catch {
    return value
  }
}
